/**
 * ApiError — the single error type thrown by the domain HTTP client (`http`).
 *
 * Every `api/<area>/<domain>/api.ts` method surfaces failures as an
 * ApiError so hooks and components can branch on `status` and show `message`
 * (parsed from the NestJS `{ statusCode, message, error }` body) without each
 * api re-implementing response parsing.
 */
/** The words a NestJS error body carries: its `message`, or the first of a list of them; null for any other body. */
function messageOf(body: unknown): string | null {
  const m = typeof body === 'object' && body !== null ? (body as { message?: unknown }).message : undefined;
  if (typeof m === 'string') return m;
  if (Array.isArray(m) && typeof m[0] === 'string') return m[0];
  return null;
}

export class ApiError extends Error {
  readonly status: number;
  /** Parsed JSON body (or raw text) of the error response, when available. */
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /**
   * The words the server's answer carried, a NestJS body's `message`, or null
   * where it carried none: an empty body, a page that is not JSON. `message`
   * falls back to the client's own words ("Request failed (500)"), which are
   * not the server's.
   */
  get serverMessage(): string | null {
    return messageOf(this.body) || null;
  }

  /** Build an ApiError from a non-OK Response, extracting the NestJS message. */
  static async fromResponse(res: Response): Promise<ApiError> {
    let body: unknown;
    let message = `Request failed (${res.status})`;
    try {
      const text = await res.text();
      if (text) {
        try {
          body = JSON.parse(text);
          message = messageOf(body) ?? message;
        } catch {
          // Non-JSON body — use the raw text as the message.
          message = text;
          body = text;
        }
      }
    } catch {
      // Body already consumed / unreadable — keep the default message.
    }
    return new ApiError(res.status, message, body);
  }
}
